import type Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import type { ClassificationConfig, LabelConfig, LegendConfig, StyleConfig } from '../presentation/config';
import type { PresentationPresetId, PresentationWorkspaceConfig, WorkspaceModule } from '../presentation/workspace';

export type AttributeValue = string | number | boolean | null;
export type OriginalAttributes = Readonly<Record<string, AttributeValue>>;
export type GeometryKind = 'Point' | 'LineString' | 'Polygon' | 'Mixed' | 'Unknown';
export type VectorSourceFormat = 'GeoJSON' | 'KML' | 'KMZ' | 'SHP' | 'GPKG' | 'WORK' | 'SUBAREA';
export type VectorGeometryType = 'Point' | 'MultiPoint' | 'LineString' | 'MultiLineString' | 'Polygon' | 'MultiPolygon' | 'GeometryCollection';
export type WorkingGeometryType = 'Point' | 'LineString' | 'Polygon';

export interface ProjectFeatureMetadata {
  readonly createdAt: string;
  readonly geometryType: WorkingGeometryType;
  /** Present only on planning objects (see domain/planningObjects). */
  readonly planning?: PlanningObjectMetadata;
}

/** Semantic preset of a planning object plus how its label is built. Technical values live in the record attributes. */
export interface PlanningObjectMetadata {
  readonly preset: string;
  readonly labelMode: 'auto' | 'custom' | 'none';
  /** Custom label, first line («Texto principal»). Projects before V1.5.0 stored their single custom text here. */
  readonly labelText?: string;
  /** Custom label, second line («Texto secundario»). */
  readonly labelSecondary?: string;
  /** Custom label: append the calculated length/area below the texts. Absent (older projects) means no. */
  readonly labelMeasure?: boolean;
  /** Optional colour override of the preset symbol. */
  readonly color?: string;
  /**
   * V1.8 «Apariencia»: only what the user changed (absent = defaults of the type). Projects before V1.8 have none and
   * show the V1.8 defaults (compact pump, labels without a box). Normalised on read and write (planningAppearance.ts).
   */
  readonly appearance?: PlanningAppearance;
}

export type PlanningLinePattern = 'auto' | 'solid' | 'dashed';
export type PlanningMeasureUnit = 'auto' | 'm' | 'km';
/** Variant of a point symbol: wells («open»/«drilled»), the Finca / Hacienda («rows» with crop rows / «plain»). */
export type PlanningWellVariant = 'open' | 'drilled' | 'rows' | 'plain';
export type PlanningRoadCenter = 'none' | 'solid' | 'dashed';
export type PlanningNoteAlign = 'left' | 'center' | 'right';
export type PlanningNoteFont = 'sans' | 'serif' | 'mono';

/** Visual settings of one planning object. Every field is optional; sizes are screen pixels/percentages, never map units. */
export interface PlanningAppearance {
  readonly symbol?: {
    /** Point symbol size, 0.4–1.6 of the type's size. */
    readonly scale?: number;
    /** 0.1–1. */
    readonly opacity?: number;
    /** «Noria / pozo» only: traditional open well or drilled well. */
    readonly variant?: PlanningWellVariant;
  };
  /** Lines and polygon outlines. */
  readonly stroke?: { readonly width?: number; readonly opacity?: number; readonly pattern?: PlanningLinePattern };
  /** Polygons. */
  readonly fill?: { readonly visible?: boolean; readonly color?: string; readonly opacity?: number };
  readonly label?: {
    readonly size?: number;
    readonly color?: string;
    readonly bold?: boolean;
    readonly halo?: boolean;
    readonly haloColor?: string;
    readonly background?: boolean;
    readonly backgroundColor?: string;
    readonly backgroundOpacity?: number;
    /** Screen-pixel offset from the automatic position (+x right, +y down), kept when the geometry moves. */
    readonly offsetX?: number;
    readonly offsetY?: number;
    /** V1.9.0 E4: clockwise angle on screen, 0–360° («manual»). */
    readonly rotation?: number;
    /** V1.9.0 E4: 'line' = along the route at the label (lines only), always kept readable; absent = 'manual'. */
    readonly orientation?: PlanningLabelOrientation;
  };
  /**
   * Lines and polygons: the calculated measure (custom labels use labelMeasure for «show»). V1.9.0 E4: drawn inside the
   * label (placement absent = 'label', as before) or as its own text with its own size, colour, angle and position.
   * The value is always recomputed from the geometry.
   */
  readonly measure?: {
    readonly show?: boolean;
    readonly unit?: PlanningMeasureUnit;
    readonly areaUnit?: PlanningAreaUnit;
    readonly placement?: PlanningMeasurePlacement;
    readonly size?: number;
    readonly color?: string;
    readonly bold?: boolean;
    /** Clockwise angle on screen, 0–360°, used when orientation is 'manual'. */
    readonly rotation?: number;
    /** 'label' (absent) = the label's angle; 'line' = along the route; 'manual' = `rotation`. */
    readonly orientation?: PlanningMeasureOrientation;
    /** Screen-pixel offset from its placement (+x right, +y down). */
    readonly offsetX?: number;
    readonly offsetY?: number;
  };
  /** V1.9.3 «Ruta de acceso / Carretera»: the body colour is the object's colour; these are its casing, centre line and arrows. */
  readonly road?: { readonly casing?: string; readonly center?: PlanningRoadCenter; readonly centerColor?: string; readonly arrows?: boolean };
  /** V1.9.3 «Insertar texto»: alignment, font, border and the call-out line of a free text (the box fill is `label.background`). */
  readonly note?: {
    readonly align?: PlanningNoteAlign; readonly font?: PlanningNoteFont;
    readonly border?: boolean; readonly borderColor?: string; readonly borderWidth?: number;
    /** Line from the point of interest (the object's point) to the text, when the text has been moved away from it. */
    readonly leader?: boolean;
  };
}

export type PlanningLabelOrientation = 'manual' | 'line';
export type PlanningMeasurePlacement = 'label' | 'above' | 'below' | 'left' | 'right' | 'free';
export type PlanningMeasureOrientation = 'label' | 'line' | 'manual';
export type PlanningAreaUnit = 'auto' | 'ha' | 'm2';

export interface PhysicalFragment {
  readonly id: string;
  readonly feature: Feature<Geometry>;
  readonly attributes: OriginalAttributes;
  readonly fincaId: string;
  readonly suerteId: string;
  readonly tablonId: string;
}

export interface ExcludeFragmentOperation {
  readonly kind: 'exclude-fragment';
  readonly fragmentId: string;
  readonly entityKind: 'suerte' | 'tablon';
  readonly entityId: string;
}

export interface SerializedPolygonGeometry {
  readonly type: 'Polygon';
  /** GeoJSON coordinates persisted in EPSG:4326. */
  readonly coordinates: readonly (readonly (readonly [number, number])[])[];
}

export interface DifferenceFragmentOperation {
  readonly kind: 'difference-fragment';
  readonly schemaVersion: 1;
  readonly fragmentId: string;
  readonly entityKind: 'suerte' | 'tablon';
  readonly entityId: string;
  readonly cutGeometry: SerializedPolygonGeometry;
}

export type GeometryEditOperation = ExcludeFragmentOperation | DifferenceFragmentOperation;

export interface GeometryEditHistory {
  readonly commands: readonly GeometryEditOperation[];
  readonly cursor: number;
}

export interface GeometryEditSession {
  readonly entityKind: 'suerte' | 'tablon';
  readonly entityId: string;
  readonly history: GeometryEditHistory;
  readonly selectedFragmentId: string | null;
}

export interface LogicalTablon {
  readonly id: string;
  readonly code: string;
  readonly suerteId: string;
  readonly fragmentIds: readonly string[];
  readonly areaHa: number;
  readonly declaredAreaHa: number;
}

export interface LogicalSuerte {
  readonly id: string;
  readonly code: string;
  readonly label: string;
  readonly fincaId: string;
  readonly tablonIds: readonly string[];
  readonly fragmentIds: readonly string[];
  readonly areaHa: number;
  readonly declaredAreaHa: number;
}

export interface LogicalFinca {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly suerteIds: readonly string[];
  readonly fragmentIds: readonly string[];
  readonly areaHa: number;
  readonly declaredAreaHa: number;
}

export interface LayerModel {
  readonly id: string;
  readonly name: string;
  readonly sourceFormat: Exclude<VectorSourceFormat, 'WORK'>;
  readonly sourceCrs: string;
  readonly readOnly: true;
  readonly geometryKind: GeometryKind;
  readonly attributeFields: readonly string[];
  readonly fragments: readonly PhysicalFragment[];
  readonly fragmentsById: ReadonlyMap<string, PhysicalFragment>;
  readonly fincas: ReadonlyMap<string, LogicalFinca>;
  readonly suertes: ReadonlyMap<string, LogicalSuerte>;
  readonly tablones: ReadonlyMap<string, LogicalTablon>;
  readonly warnings: readonly string[];
}

export interface VectorFeatureRecord {
  readonly id: string;
  readonly feature: Feature<Geometry>;
  readonly attributes: OriginalAttributes;
  readonly projectMetadata?: ProjectFeatureMetadata;
}

export interface VectorLayerStyle {
  readonly fillColor: string;
  readonly strokeColor: string;
  readonly strokeWidth: number;
  readonly pointRadius: number;
}

export interface VectorLayerModel {
  readonly id: string;
  readonly kind: 'master' | 'generic' | 'working' | 'subarea';
  readonly name: string;
  readonly sourceName: string;
  readonly sourceFormat: VectorSourceFormat;
  readonly sourceCrs: string;
  readonly assumedCrs: boolean;
  readonly geometryTypes: readonly VectorGeometryType[];
  readonly attributeFields: readonly string[];
  readonly features: readonly VectorFeatureRecord[];
  readonly featuresById: ReadonlyMap<string, VectorFeatureRecord>;
  readonly extent: readonly [number, number, number, number] | null;
  readonly visible: boolean;
  readonly opacity: number;
  readonly locked: boolean;
  readonly order: number;
  readonly style: VectorLayerStyle;
  readonly warnings: readonly string[];
  readonly masterModel?: LayerModel;
  readonly workingGeometryType?: WorkingGeometryType;
  readonly scopeMode: 'none' | 'attributes' | 'spatial';
  /** Working layer of planning objects: mixed points, lines and polygons with semantic presets. */
  readonly planning?: boolean;
}

export interface GenericSelection {
  readonly layerId: string;
  readonly featureId: string;
}

export type MapTool = 'navigate' | 'select' | 'select-multiple' | 'select-box' | 'move-labels' | 'measure-distance' | 'measure-area' | 'draw-point' | 'draw-line' | 'draw-polygon' | 'draw-rectangle';

export type SelectionEntry =
  | { readonly source: 'master'; readonly layerId: string; readonly kind: SelectionLevel; readonly id: string }
  | { readonly source: 'generic'; readonly layerId: string; readonly kind: 'feature'; readonly id: string };

export interface SelectionSet {
  readonly layerId: string | null;
  readonly entries: readonly SelectionEntry[];
  readonly focus: SelectionEntry | null;
}

export type FieldSource = 'system' | 'source' | 'join' | 'project';
export type FieldValueType = 'string' | 'number' | 'date' | 'boolean';
export type EntityLevel = 'fragment' | 'tablon' | 'suerte' | 'finca' | 'feature' | 'subarea';

export interface OperationalSubarea {
  readonly id: string;
  readonly name: string;
  readonly parentLevel: 'suerte' | 'tablon';
  readonly parentId: string;
  readonly fincaId: string;
  readonly fincaName: string;
  readonly codfinca: string;
  readonly suerteId: string;
  readonly codSuerte: string;
  readonly tablonId?: string;
  readonly tablon?: string;
  readonly feature: Feature<Geometry>;
  readonly createdAt: string;
  readonly areaM2: number;
  readonly areaHa: number;
  readonly coveragePercent: number;
  readonly parentGeometryAreaM2: number;
  readonly parentDeclaredAreaHa?: number;
}

export interface FieldDescriptor {
  readonly id: string;
  readonly name: string;
  readonly source: FieldSource;
  readonly type: FieldValueType;
  readonly level: EntityLevel;
  readonly sourceName?: string;
  readonly suggestedValues?: readonly string[];
}

export interface OperationalAttributeField {
  readonly descriptor: FieldDescriptor;
  readonly valuesByEntityId: Readonly<Record<string, AttributeValue>>;
}
export interface ExcelJoinKeyExpressionRecord {
  readonly components: readonly string[];
  readonly separator: string;
}
export interface ExcelJoinKeyMappingRecord {
  readonly target: ExcelJoinKeyExpressionRecord;
  readonly source: ExcelJoinKeyExpressionRecord;
}
export interface ExcelJoinRecord {
  readonly fileName: string;
  readonly sheet: string;
  readonly level: EntityViewLevel;
  readonly targetLayerId?: string;
  readonly targetEntityLevel?: EntityViewLevel;
  readonly keyColumns: readonly string[];
  readonly keyMappings?: readonly ExcelJoinKeyMappingRecord[];
  readonly selectedColumns: readonly string[];
  readonly normalization?: {
    readonly trim: boolean; readonly collapseWhitespace: boolean; readonly caseInsensitive: boolean;
    readonly ignoreDiacritics: boolean; readonly numericEquivalence: boolean;
  };
  readonly source?: { readonly fileName: string; readonly sheet: string; readonly rowCount: number; readonly columns: readonly string[] };
  readonly audit: {
    readonly totalRows: number; readonly matched: number; readonly unmatched: number; readonly emptyKeys: number;
    readonly duplicates: readonly string[]; readonly conflicts: readonly string[]; readonly newColumns: readonly string[]; readonly collisions: readonly string[]; readonly blocked: boolean;
  };
}
export interface ProjectMetadata { readonly id: string; readonly name: string; readonly createdAt: string; readonly updatedAt: string }
export type AttributeFilterOperator = 'one-of' | 'not-one-of' | 'eq' | 'gt' | 'gte' | 'lt' | 'lte' | 'between';
export interface AttributeFilter {
  readonly id: string;
  readonly fieldId: string;
  readonly operator: AttributeFilterOperator;
  readonly values: readonly AttributeValue[];
  readonly value?: number;
  readonly upperValue?: number;
}
export type AttributeFilterSet = readonly AttributeFilter[];

export type Selection =
  | { readonly kind: 'finca'; readonly id: string }
  | { readonly kind: 'suerte'; readonly id: string }
  | { readonly kind: 'tablon'; readonly id: string }
  | { readonly kind: 'fragment'; readonly id: string }
  | null;

export type EntityViewLevel = 'finca' | 'suerte' | 'tablon' | 'fragment';
export type LabelMode = 'fincas' | 'suerte' | 'suerte_tablones' | 'tablones' | 'fragmentos' | 'seleccion' | 'none';
/** @deprecated Use EntityViewLevel. Kept as a source-compatible alias for v1 integrations. */
export type SelectionLevel = EntityViewLevel;
export type ViewScope = 'all' | 'finca' | 'selection';
export interface WorkScope {
  readonly fincaIds: readonly string[];
  readonly suerteIds: readonly string[];
  readonly tablonIds: readonly string[];
}
export type RemainderVisibility = 'hide' | 'dim';
export type BasemapId = 'none' | 'cartographic' | 'satellite' | 'hybrid';

export interface HybridReferenceVisibility {
  readonly transportation: boolean;
  readonly places: boolean;
  readonly hydrography: boolean;
}

export type CasurHydricKind = 'channels' | 'drainage';
export type CasurHydricClass = 'principal' | 'secondary' | 'tertiary' | 'unclassified';
export type CasurHydricStatus = 'existing' | 'projected' | 'unknown';

export interface CasurHydricVisibility {
  readonly channels: boolean;
  readonly drainage: boolean;
}

export interface CasurHydricImportMetadata {
  readonly sourceFileName: string;
  readonly importedAt: string;
  readonly fingerprint: string;
  readonly sourceCrs: readonly string[];
  readonly channelCount: number;
  readonly drainageCount: number;
  readonly warnings: readonly string[];
  readonly schemaVersion: 1;
}

export interface CasurHydricReference {
  readonly channels: VectorLayerModel;
  readonly drainage: VectorLayerModel;
  readonly metadata: CasurHydricImportMetadata;
}

export interface CasurHydricSelection {
  readonly kind: CasurHydricKind;
  readonly featureId: string;
}

export const defaultCasurHydricVisibility: CasurHydricVisibility = { channels: false, drainage: false };

export const normalizeCasurHydricVisibility = (value: unknown): CasurHydricVisibility => {
  const candidate = value && typeof value === 'object' ? value as Partial<CasurHydricVisibility> : {};
  return {
    channels: typeof candidate.channels === 'boolean' ? candidate.channels : false,
    drainage: typeof candidate.drainage === 'boolean' ? candidate.drainage : false,
  };
};

export const defaultHybridReferenceVisibility: HybridReferenceVisibility = {
  transportation: true,
  places: true,
  hydrography: true,
};

export const normalizeHybridReferenceVisibility = (value: unknown): HybridReferenceVisibility => {
  const candidate = value && typeof value === 'object' ? value as Partial<HybridReferenceVisibility> : {};
  return {
    transportation: typeof candidate.transportation === 'boolean' ? candidate.transportation : true,
    places: typeof candidate.places === 'boolean' ? candidate.places : true,
    hydrography: typeof candidate.hydrography === 'boolean' ? candidate.hydrography : true,
  };
};

export const normalizeBasemapId = (value: unknown): BasemapId =>
  value === 'none' || value === 'cartographic' || value === 'satellite' || value === 'hybrid'
    ? value
    : 'cartographic';

export interface SavedSelection {
  readonly id: string;
  readonly name: string;
  readonly layerId: string;
  readonly entries: readonly SelectionEntry[];
  readonly createdAt: string;
}

export interface FloatingMapToolbarState {
  readonly position: 'default' | 'free';
  readonly x: number;
  readonly y: number;
}

export interface ContextualToolbarPosition {
  readonly position: 'default' | 'free';
  readonly x: number;
  readonly y: number;
}

export type AttributeTableMode = 'compact' | 'expanded';

export interface WorkspaceLayout {
  readonly leftPanelWidth: number;
  readonly leftPanelCollapsed: boolean;
  readonly rightPanelWidth: number;
  readonly rightPanelCollapsed: boolean;
  readonly attributeTableOpen: boolean;
  /** Open table shown as its header strip only (compact) or with rows at `attributeTableHeight` (expanded). */
  readonly attributeTableMode: AttributeTableMode;
  readonly attributeTableHeight: number;
  readonly contextualToolbarPosition: ContextualToolbarPosition;
}

/**
 * V1.9.0 classification scenario («Propuesta»): proposed categories of the principal classification for some Suertes or
 * Tablones. It never writes the Excel, the project fields, the Maestro or its geometry; «Actual» is the classification
 * as it is. Keys are logical entity ids (`finca:…/suerte:…`, `…/tablon:…`).
 */
export interface ScenarioOverrides {
  readonly suerte: Readonly<Record<string, string>>;
  readonly tablon: Readonly<Record<string, string>>;
}
export interface ClassificationScenario {
  readonly id: string;
  readonly name: string;
  /** Principal classification field (and split part) the overrides apply to. */
  readonly field: string;
  readonly splitPart?: 'primary' | 'secondary';
  readonly overrides: ScenarioOverrides;
  readonly createdAt: string;
  readonly updatedAt: string;
}
/** Where the single «ESCENARIO PROPUESTO · … · No ejecutado» mark goes (map and PNG). */
export type ScenarioMark = 'legend' | 'top' | 'bottom';

export interface ScenarioState {
  /** Shown scenario; null = «Actual». */
  readonly active: string | null;
  readonly items: readonly ClassificationScenario[];
  /** Absent = 'legend' (a badge on the map only when no legend is drawn). */
  readonly mark?: ScenarioMark;
}

export interface AppState {
  readonly project: ProjectMetadata;
  readonly projectDirty: boolean;
  readonly model: LayerModel | null;
  /** Immutable hierarchy rebuilt directly from the persisted/imported Maestro dataset. */
  readonly sourceModel: LayerModel | null;
  readonly geometryEdits: GeometryEditHistory;
  /** Transient draft. It is deliberately omitted from .casurgeo persistence. */
  readonly geometryEditSession: GeometryEditSession | null;
  readonly layers: readonly VectorLayerModel[];
  readonly activeLayerId: string | null;
  readonly genericSelection: GenericSelection | null;
  readonly selection: Selection;
  readonly selectionSet: SelectionSet;
  readonly activeTool: MapTool;
  /** Single cartographic grain governing map, selection, table, labels and thematic counts. */
  readonly entityViewLevel: EntityViewLevel;
  /** Legacy persisted alias. It is always synchronized with entityViewLevel. */
  readonly selectionLevel: SelectionLevel;
  readonly viewScope: ViewScope;
  readonly activeFincaId: string | null;
  readonly workScope: WorkScope;
  readonly remainderVisibility: RemainderVisibility;
  readonly labelMode: LabelMode;
  readonly labelConfig: LabelConfig;
  readonly styleConfig: StyleConfig;
  readonly classificationConfig: ClassificationConfig;
  readonly thematicSchemes: readonly ClassificationConfig[];
  readonly legendConfig: LegendConfig;
  readonly activeWorkspaceModule: WorkspaceModule;
  readonly savedSelections: readonly SavedSelection[];
  readonly activePresentationPreset: PresentationPresetId;
  readonly presentationWorkspace: PresentationWorkspaceConfig;
  readonly presentationCaptureMode: boolean;
  readonly floatingMapToolbar: FloatingMapToolbarState;
  readonly workspaceLayout: WorkspaceLayout;
  readonly operationalFields: readonly OperationalAttributeField[];
  readonly excelJoins: readonly ExcelJoinRecord[];
  readonly subareas: readonly OperationalSubarea[];
  /** V1.9.0 classification scenarios (absent in older projects = only «Actual»). */
  readonly scenarios: ScenarioState;
  readonly subareaLabelsVisible: boolean;
  readonly attributeFilters: AttributeFilterSet;
  readonly basemap: BasemapId;
  readonly hybridReference: HybridReferenceVisibility;
  readonly casurHydricVisibility: CasurHydricVisibility;
  readonly casurHydricReference: CasurHydricReference | null;
  readonly casurHydricSelection: CasurHydricSelection | null;
  readonly casurHydricLoading: boolean;
  readonly casurHydricError: string | null;
  readonly loadingMessage: string | null;
  readonly error: string | null;
}
